// src/modulos/Cadastros/RawMaterialsPage.js

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useFirestore from '../../hooks/useFirestore';
import useSubCollection from '../../hooks/useSubCollection';
import usePagePermissions from '../../hooks/usePagePermissions';
import styles from './Registers.module.css';

// React Icons
import {
    FiPlus,
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiSearch,
    FiPackage,
    FiUser,
    FiClipboard,
    FiCalendar,
    FiTag,
    FiMapPin,
    FiList,
    FiEye,
    FiFileText,
    FiPrinter,
    FiArrowLeft,
    FiArrowRight,
    FiDownload,
    FiUpload,
    FiDollarSign,
    FiPercent,
    FiBox,
    FiHome,
    FiGrid,
    FiShoppingCart,
    FiTruck,
    FiMail,
    FiPhone,
    FiGlobe,
    FiStar,
    FiAward,
    FiHeart,
    FiShield,
    FiBook,
    FiChevronDown
} from 'react-icons/fi';
import {
    FaBox,
    FaCogs,
    FaBalanceScale,
    FaWeight,
    FaRuler,
    FaClock,
    FaIndustry,
    FaShoppingCart,
    FaDollarSign,
    FaPercent,
    FaCheckCircle,
    FaExclamationTriangle,
    FaInfoCircle,
    FaTags,
    FaLayerGroup,
    FaMicroscope,
    FaFlask,
    FaTint,
    FaBolt,
    FaCertificate,
    FaBuilding,
    FaUsers,
    FaFileAlt,
    FaRegFilePdf
} from 'react-icons/fa';
import { IoCheckmarkOutline, IoDocumentText } from 'react-icons/io5';
import { SlChemistry } from 'react-icons/sl';
import { GrDocumentTransfer, GrUpload, GrDownload } from 'react-icons/gr';
import { MdCategory, MdBusiness, MdDescription, MdLocationOn, MdAttachMoney } from 'react-icons/md';

// Common Components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
    Toast,
    TagInput,
    ComboBox,
} from '../../components/common';

import useToast from '../../hooks/useToast';
import usePagination from '../../hooks/usePagination';

// 1. Constantes e Definicoes da Colecao
const RAW_MATERIALS_COLLECTION = 'raw-materials';
const ITEMS_PER_PAGE = 10;

// Constantes para as colecoes de parametros
const PRODUCT_TYPE_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'tipo-produto'
};
const CATEGORY_COLLECTION = 'parameters/data/product-categories';
const SUFFIX_COLLECTION = 'parameters/data/sufixos';
const SUPPLIERS_COLLECTION = 'suppliers';
const UNITS_COLLECTION = 'parameters/data/unidades-medida';
const NCM_COLLECTION = 'parameters/data/ncm';
const COLORS_COLLECTION = 'parameters/data/cores';

const SITUATION_OPTIONS = ['Ativo', 'Inativo'];
const DEFAULT_SITUATION = SITUATION_OPTIONS[0];

const ORIGEM_MERCADORIA_ICMS_OPTIONS = [
    { value: 0, label: '0 - Nacional, exceto os indicados nos codigos 3, 4, 5 e 8' },
    { value: 1, label: '1 - Estrangeira - Importacao direta, exceto a indicada no codigo 6' },
    { value: 2, label: '2 - Estrangeira - Adquirida no mercado interno, exceto a indicada no codigo 7' },
    { value: 3, label: '3 - Nacional, mercadoria ou bem com conteudo de importacao superior a 40% e inferior ou igual 70%' },
    { value: 4, label: '4 - Nacional, cuja producao tenha sido feita em conformidade com os processos produtivos basicos' },
    { value: 5, label: '5 - Nacional, mercadoria ou bem com conteudo de importacao inferior ou igual a 40%' },
    { value: 6, label: '6 - Estrangeira, Importacao direta sem similar nacional, constante em lista CAMEX e gas natural' },
    { value: 7, label: '7 - Estrangeira, Adquirida no mercado interno s/ similar nacional, constante em lista CAMEX e gas natural' },
];
const DEFAULT_ORIGEM_ICMS = ORIGEM_MERCADORIA_ICMS_OPTIONS[0].value;

const CURRENCY_OPTIONS = [
    { value: 'BRL', label: 'Real (BRL)' },
    { value: 'USD', label: 'Dólar Americano (USD)' },
    { value: 'EUR', label: 'Euro (EUR)' },
];

const NEW_SUPPLIER_RELATION_MODEL = {
    supplierId: '',
    razaoSocial: '',
    codigo: '',
    origemIcms: DEFAULT_ORIGEM_ICMS,
    unitOfMeasure: '',
    valor: '',
    pesoEspecifico: '',
    data: new Date().toISOString().split('T')[0],
    descricao: '',
    leadTime: '',
    loteMinimo: '',
};

// Hook personalizado para debounce
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

// Hook para lazy loading de parametros
const useLazyFirestore = (collectionName, orderField = null, enabled = false) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!enabled || loaded || !collectionName) return;

        const loadData = async () => {
            setLoading(true);
            try {
                const { db } = await import('../../firebaseConfig');
                const { collection, query, orderBy, getDocs } = await import('firebase/firestore');

                let q = collection(db, collectionName);
                if (orderField) {
                    q = query(q, orderBy(orderField));
                }

                const querySnapshot = await getDocs(q);
                const items = [];
                querySnapshot.forEach((document) => {
                    items.push({ id: document.id, ...document.data() });
                });
                setData(items);
                setLoaded(true);
            } catch (err) {
                console.error(`Erro ao carregar ${collectionName}:`, err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [collectionName, orderField, enabled, loaded]);

    return { data, loading, loaded };
};

// Componente de linha da tabela memoizado
const RawMaterialRow = React.memo(({
    item,
    productTypes,
    categories,
    formLoading,
    onEdit,
    onDelete,
    can,
    t,
    ncmList
}) => {
    const getParamName = (id, paramsList) => {
        if (!id || !paramsList || paramsList.length === 0) return t('common.notInformed');
        const paramItem = paramsList.find(p => p.id === id);
        return paramItem ? paramItem.name : t('common.notInformed');
    };

    return (
        <tr className={styles.tableRow}>
            <td><strong>{item.code}</strong></td>
            <td>{item.commercialName}</td>
            <td>{item.inciName || t('common.notInformed')}</td>
            <td>
                {item.productTypeIds?.map(id => getParamName(id, productTypes)).join(', ') || t('common.notInformed')}
            </td>
            <td>
                {item.categoryIds?.map(id => getParamName(id, categories)).join(', ') || t('common.notInformed')}
            </td>
            <td>
                <small>
                    {item.suppliers?.map(s => s.razaoSocial).join(', ') || t('pages.rawMaterials.table.noSuppliers')}
                </small>
            </td>
            <td className={styles.priceCell}>
                {item.custoCompra ? Number(item.custoCompra).toFixed(2) : Number(0).toFixed(2)}
            </td>
            <td>
                <div className={styles.actionsContainer}>
                    {can?.edit && (
                        <button
                            onClick={() => onEdit(item)}
                            className={`${styles.actionButton} ${styles.editButton}`}
                            disabled={formLoading}
                            title={t('common.edit')}
                        >
                            <FiEdit size={16} />
                        </button>
                    )}
                    {can?.delete && (
                        <button
                            onClick={() => onDelete(item)}
                            className={`${styles.actionButton} ${styles.deleteButton}`}
                            disabled={formLoading}
                            title={t('common.delete')}
                        >
                            <FiTrash2 size={16} />
                        </button>
                    )}
                    {!can?.edit && !can?.delete && (
                        <span className={styles.textMuted}>-</span>
                    )}
                </div>
            </td>
        </tr>
    );
});
RawMaterialRow.displayName = 'RawMaterialRow';

const RawMaterialPage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('materiaprima');
    const { toasts, addToast, removeToast } = useToast();

    // 2. Estados Locais para o Formulario
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [code, setCode] = useState('');
    const [commercialName, setCommercialName] = useState('');
    const [inciName, setInciName] = useState('');
    const [inciNamePt, setInciNamePt] = useState('');
    const [situation, setSituation] = useState(DEFAULT_SITUATION);
    const [casNumber, setCasNumber] = useState('');

    const [selectedProductTypeIds, setSelectedProductTypeIds] = useState([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
    const [selectedPrefix, setSelectedPrefix] = useState('');
    const [selectedCriteria, setSelectedCriteria] = useState([]);

    const [selectedSuppliers, setSelectedSuppliers] = useState([]);
    const [selectedSupplierId, setSelectedSupplierId] = useState(null);

    // Estado para dados fiscais
    const [codigoBarras, setCodigoBarras] = useState('');
    const [matInsumoInterno, setMatInsumoInterno] = useState('');
    const [unidadeMedidaInterna, setUnidadeMedidaInterna] = useState('');
    const [encargosFinanceiros, setEncargosFinanceiros] = useState('');
    const [custoCompra, setCustoCompra] = useState('');
    const [precoFormulacao, setPrecoFormulacao] = useState('');
    const [moeda, setMoeda] = useState('BRL');
    const [dataUltimoAumento, setDataUltimoAumento] = useState('');
    const [qtdeMinimaEstoque, setQtdeMinimaEstoque] = useState('');
    const [pesoEmbalagem, setPesoEmbalagem] = useState('');
    const [corPadrao, setCorPadrao] = useState('');
    const [naturezaQuimica, setNaturezaQuimica] = useState('');
    const [versaoArteGrafica, setVersaoArteGrafica] = useState('');
    const [mgKg, setMgKg] = useState('');
    const [altura, setAltura] = useState('');
    const [ncm, setNcm] = useState('');
    const [origemMercadoriaPadrao, setOrigemMercadoriaPadrao] = useState(DEFAULT_ORIGEM_ICMS);
    const [passaQualidade, setPassaQualidade] = useState(false);
    const [ultimoValor, setUltimoValor] = useState('');
    const [qtdeMaximaEstoque, setQtdeMaximaEstoque] = useState('');
    const [dataUltimaEntrada, setDataUltimaEntrada] = useState('');
    const [densidadePesoEspecifico, setDensidadePesoEspecifico] = useState('');
    const [percentualDiferimento, setPercentualDiferimento] = useState('');
    const [material, setMaterial] = useState('');
    const [purezaMateriaPrima, setPurezaMateriaPrima] = useState('');
    const [largura, setLargura] = useState('');
    const [dl50Oral, setDl50Oral] = useState('');

    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    const [filterProductTypeId, setFilterProductTypeId] = useState('');
    const [filterCategoryId, setFilterCategoryId] = useState('');

    const [shouldLoadModalParams, setShouldLoadModalParams] = useState(false);

    // 3. Hooks de Dados
    const {
        data: rawMaterials,
        loading: listLoading,
        addDocument,
        updateDocument,
        deleteDocument
    } = useFirestore(RAW_MATERIALS_COLLECTION, 'code');

    const { data: productTypes } = useSubCollection(
        PRODUCT_TYPE_COLLECTION_PATH.parentCollection,
        PRODUCT_TYPE_COLLECTION_PATH.parentId,
        PRODUCT_TYPE_COLLECTION_PATH.subCollectionName,
        'name'
    );

    const { data: categories, loading: categoriesLoading } = useFirestore(CATEGORY_COLLECTION, 'order');

    const { data: suffixes } = useLazyFirestore(SUFFIX_COLLECTION, 'name', shouldLoadModalParams);
    const { data: allSuppliers } = useLazyFirestore(SUPPLIERS_COLLECTION, 'razaoSocial', shouldLoadModalParams);
    const { data: allUnits } = useLazyFirestore(UNITS_COLLECTION, 'name', shouldLoadModalParams);
    const { data: ncmList, loading: ncmLoading } = useFirestore(NCM_COLLECTION, 'ncmCode');
    const { data: colorsList } = useLazyFirestore(COLORS_COLLECTION, 'name', shouldLoadModalParams);

    const isLoading = listLoading || categoriesLoading;

    const sortedCategories = useMemo(() => {
        if (!categories) return [];
        return [...categories].sort((a, b) => a.name.localeCompare(b.name));
    }, [categories]);

    const getParamName = (id, paramsList) => {
        if (!id || !paramsList || paramsList.length === 0) return t('common.notInformed');
        const item = paramsList.find(p => p.id === id);
        return item ? item.name : t('common.notInformed');
    };

    // Prepare NCM options for combobox
    const ncmOptions = useMemo(() => {
        if (!ncmList) return [];
        return ncmList
            .sort((a, b) => (a.ncmCode || '').localeCompare(b.ncmCode || ''))
            .map(n => ({
                id: n.id,
                ncmCode: n.ncmCode || '',
                ncmDescription: n.ncmDescription || '',
                label: `${n.ncmCode || ''}${n.ncmDescription ? ` - ${n.ncmDescription}` : ''}`
            }));
    }, [ncmList]);

    // Prepare supplier options for combobox
    const supplierOptions = useMemo(() => {
        if (!allSuppliers) return [];
        const selectedIds = new Set(selectedSuppliers.map(s => s.supplierId));
        return allSuppliers
            .filter(supplier => !selectedIds.has(supplier.id))
            .map(s => ({
                id: s.id,
                codigo: s.codigo || '',
                razaoSocial: s.razaoSocial || '',
                label: `${s.codigo || ''} - ${s.razaoSocial}`
            }));
    }, [allSuppliers, selectedSuppliers]);

    const getNextSequentialCode = useCallback((prefix) => {
        if (!rawMaterials) return `${prefix}001`;

        const existingNumbers = rawMaterials
            .filter(item => item.code && item.code.startsWith(prefix))
            .map(item => parseInt(item.code.slice(prefix.length), 10))
            .filter(num => !isNaN(num))
            .sort((a, b) => a - b);

        if (existingNumbers.length === 0) {
            return `${prefix}001`;
        }

        let nextNumber = 1;
        for (let i = 0; i < existingNumbers.length; i++) {
            if (existingNumbers[i] > nextNumber) {
                break;
            }
            if (existingNumbers[i] === nextNumber) {
                nextNumber++;
            }
        }

        return `${prefix}${nextNumber.toString().padStart(3, '0')}`;
    }, [rawMaterials]);

    const previewGeneratedCode = useMemo(() => {
        if (!selectedPrefix) return t('pages.rawMaterials.form.selectSuffix');
        return getNextSequentialCode(selectedPrefix);
    }, [selectedPrefix, getNextSequentialCode, t]);

    useEffect(() => {
        if (!editingItem && selectedPrefix) {
            setCode(previewGeneratedCode);
        }
    }, [editingItem, selectedPrefix, previewGeneratedCode]);

    // Handle Enter key for saving
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (isModalOpen && !isSubmitting && !formSuccess) {
                const activeElement = document.activeElement;
                const isComboboxInput = activeElement?.closest?.('[class*="ComboboxWrapper"]') ||
                    activeElement?.closest?.('[class*="ncmComboboxWrapper"]');

                if (e.key === 'Enter' && !e.shiftKey && !isComboboxInput) {
                    e.preventDefault();
                    const form = document.querySelector('form');
                    if (form) {
                        setTimeout(() => {
                            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                        }, 100);
                    }
                }
            }
        };

        document.addEventListener('keydown', handleGlobalKeyDown);
        return () => document.removeEventListener('keydown', handleGlobalKeyDown);
    }, [isModalOpen, isSubmitting, formSuccess]);

    // --- Handlers ---
    const handleAddProductType = useCallback((option) => {
        if (option && !selectedProductTypeIds.includes(option.id)) {
            setSelectedProductTypeIds(prev => [...prev, option.id]);
        }
    }, [selectedProductTypeIds]);

    const handleRemoveProductType = useCallback((option) => {
        setSelectedProductTypeIds(prev => prev.filter(id => id !== option.id));
    }, []);

    const handleAddCategory = useCallback((option) => {
        if (option && !selectedCategoryIds.includes(option.id)) {
            setSelectedCategoryIds(prev => [...prev, option.id]);
        }
    }, [selectedCategoryIds]);

    const handleRemoveCategory = useCallback((option) => {
        setSelectedCategoryIds(prev => prev.filter(id => id !== option.id));
    }, []);

    const hasDataChanged = useCallback((newData, oldData) => {
        return JSON.stringify(newData) !== JSON.stringify(oldData);
    }, []);

    const resetForm = () => {
        setEditingItem(null);
        setIsModalOpen(false);
        setShouldLoadModalParams(false);
        setCode('');
        setCommercialName('');
        setInciName('');
        setInciNamePt('');
        setSituation(DEFAULT_SITUATION);
        setCasNumber('');
        setSelectedProductTypeIds([]);
        setSelectedCategoryIds([]);
        setSelectedPrefix('');
        setSelectedCriteria([]);
        setSelectedSuppliers([]);
        setSelectedSupplierId(null);
        setCodigoBarras('');
        setMatInsumoInterno('');
        setUnidadeMedidaInterna('');
        setEncargosFinanceiros('');
        setCustoCompra('');
        setPrecoFormulacao('');
        setMoeda('BRL');
        setDataUltimoAumento('');
        setQtdeMinimaEstoque('');
        setPesoEmbalagem('');
        setCorPadrao('');
        setNaturezaQuimica('');
        setVersaoArteGrafica('');
        setMgKg('');
        setAltura('');
        setNcm('');
        setOrigemMercadoriaPadrao(DEFAULT_ORIGEM_ICMS);
        setPassaQualidade(false);
        setUltimoValor('');
        setQtdeMaximaEstoque('');
        setDataUltimaEntrada('');
        setDensidadePesoEspecifico('');
        setPercentualDiferimento('');
        setMaterial('');
        setPurezaMateriaPrima('');
        setLargura('');
        setDl50Oral('');
        setFormError('');
        setFormSuccess('');
        setIsSubmitting(false);
    };

    const handleNewClick = () => {
        if (!can?.create) {
            addToast(t('pages.rawMaterials.noPermissionCreate'), 'error');
            return;
        }
        setEditingItem(null);
        resetForm();
        setShouldLoadModalParams(true);
        setIsModalOpen(true);
    };

    const handleEditClick = (item) => {
        if (!can?.edit) {
            addToast(t('pages.rawMaterials.noPermissionEdit'), 'error');
            return;
        }
        setEditingItem(item);
        setShouldLoadModalParams(true);
        setIsModalOpen(true);
        setCode(item.code || '');
        setCommercialName(item.commercialName || '');
        setInciName(item.inciName || '');
        setInciNamePt(item.inciNamePt || '');
        setSituation(item.situation || DEFAULT_SITUATION);
        setCasNumber(item.casNumber || '');
        setSelectedProductTypeIds(Array.isArray(item.productTypeIds) ? item.productTypeIds : []);
        setSelectedCategoryIds(Array.isArray(item.categoryIds) ? item.categoryIds : []);
        const existingPrefix = item.code ? item.code.substring(0, 3) : '';
        setSelectedPrefix(existingPrefix);
        setSelectedCriteria(item.qualityCriteria || []);
        setCodigoBarras(item.codigoBarras || '');
        setMatInsumoInterno(item.matInsumoInterno || '');
        setUnidadeMedidaInterna(item.unidadeMedidaInterna || '');
        setEncargosFinanceiros(item.encargosFinanceiros?.toString() || '');
        setCustoCompra(item.custoCompra?.toString() || '');
        setPrecoFormulacao(item.precoFormulacao?.toString() || '');
        setMoeda(item.moeda || 'BRL');
        setDataUltimoAumento(item.dataUltimoAumento || '');
        setQtdeMinimaEstoque(item.qtdeMinimaEstoque?.toString() || '');
        setPesoEmbalagem(item.pesoEmbalagem?.toString() || '');
        setCorPadrao(item.corPadrao || '');
        setNaturezaQuimica(item.naturezaQuimica || '');
        setVersaoArteGrafica(item.versaoArteGrafica || '');
        setMgKg(item.mgKg?.toString() || '');
        setAltura(item.altura?.toString() || '');
        setNcm(item.ncm || '');
        setOrigemMercadoriaPadrao(item.origemMercadoriaPadrao ?? DEFAULT_ORIGEM_ICMS);
        setPassaQualidade(item.passaQualidade || false);
        setUltimoValor(item.ultimoValor?.toString() || '');
        setQtdeMaximaEstoque(item.qtdeMaximaEstoque?.toString() || '');
        setDataUltimaEntrada(item.dataUltimaEntrada || '');
        setDensidadePesoEspecifico(item.densidadePesoEspecifico?.toString() || '');
        setPercentualDiferimento(item.percentualDiferimento?.toString() || '');
        setMaterial(item.material || '');
        setPurezaMateriaPrima(item.purezaMateriaPrima?.toString() || '');
        setLargura(item.largura?.toString() || '');
        setDl50Oral(item.dl50Oral || '');

        setSelectedSuppliers(item.suppliers?.map(s => ({
            ...s,
            valor: s.valor?.toString() || '',
            pesoEspecifico: s.pesoEspecifico?.toString() || '',
            leadTime: s.leadTime?.toString() || '',
            loteMinimo: s.loteMinimo?.toString() || ''
        })) || []);
        setSelectedSupplierId(null);
    };

    const handleSupplierSelect = (supplierId) => {
        if (!supplierId) return;
        const supplierToAdd = allSuppliers.find(s => s.id === supplierId);
        if (!supplierToAdd) return;
        const newEntry = {
            ...NEW_SUPPLIER_RELATION_MODEL,
            supplierId: supplierToAdd.id,
            razaoSocial: supplierToAdd.razaoSocial,
            codigo: supplierToAdd.codigo || '',
        };
        setSelectedSuppliers(prev => [...prev, newEntry]);
        setSelectedSupplierId(null);
    };

    const handleRemoveSupplier = (supplierIdToRemove) => {
        setSelectedSuppliers(prev => prev.filter(s => s.supplierId !== supplierIdToRemove));
    };

    const handleSupplierDataChange = (supplierId, field, value) => {
        setSelectedSuppliers(prev =>
            prev.map(supplier =>
                supplier.supplierId === supplierId
                    ? { ...supplier, [field]: value }
                    : supplier
            )
        );
    };

    const isCodeDuplicate = useCallback((codeToCheck, excludeId = null) => {
        if (!rawMaterials) return false;
        return rawMaterials.some(item =>
            item.code === codeToCheck && item.id !== excludeId
        );
    }, [rawMaterials]);

    const buildCurrentDataObject = useCallback(() => {
        return {
            code: code.trim(),
            commercialName: commercialName.trim(),
            inciName: inciName.trim(),
            inciNamePt: inciNamePt.trim(),
            situation: situation,
            casNumber: casNumber.trim(),
            productTypeIds: selectedProductTypeIds,
            categoryIds: selectedCategoryIds,
            qualityCriteria: selectedCriteria,
            codigoBarras: codigoBarras.trim(),
            matInsumoInterno: matInsumoInterno.trim(),
            unidadeMedidaInterna: unidadeMedidaInterna,
            encargosFinanceiros: parseFloat(encargosFinanceiros) || 0,
            custoCompra: parseFloat(custoCompra) || 0,
            precoFormulacao: parseFloat(precoFormulacao) || 0,
            moeda: moeda,
            dataUltimoAumento: dataUltimoAumento,
            qtdeMinimaEstoque: parseFloat(qtdeMinimaEstoque) || 0,
            pesoEmbalagem: parseFloat(pesoEmbalagem) || 0,
            corPadrao: corPadrao,
            naturezaQuimica: naturezaQuimica.trim(),
            versaoArteGrafica: versaoArteGrafica.trim(),
            mgKg: parseFloat(mgKg) || 0,
            altura: parseFloat(altura) || 0,
            ncm: ncm,
            origemMercadoriaPadrao: parseInt(origemMercadoriaPadrao) || DEFAULT_ORIGEM_ICMS,
            passaQualidade: passaQualidade,
            ultimoValor: parseFloat(ultimoValor) || 0,
            qtdeMaximaEstoque: parseFloat(qtdeMaximaEstoque) || 0,
            dataUltimaEntrada: dataUltimaEntrada,
            densidadePesoEspecifico: parseFloat(densidadePesoEspecifico) || 0,
            percentualDiferimento: parseFloat(percentualDiferimento) || 0,
            material: material.trim(),
            purezaMateriaPrima: parseFloat(purezaMateriaPrima) || 0,
            largura: parseFloat(largura) || 0,
            dl50Oral: dl50Oral.trim(),
            suppliers: selectedSuppliers.map(s => ({
                ...s,
                valor: parseFloat(String(s.valor).replace(',', '.')) || 0,
                pesoEspecifico: parseFloat(String(s.pesoEspecifico).replace(',', '.')) || 0,
                leadTime: parseInt(s.leadTime, 10) || 0,
                loteMinimo: parseFloat(String(s.loteMinimo).replace(',', '.')) || 0,
            })),
        };
    }, [code, commercialName, inciName, inciNamePt, situation, casNumber, selectedProductTypeIds, selectedCategoryIds, selectedCriteria, selectedSuppliers, codigoBarras, matInsumoInterno, unidadeMedidaInterna, encargosFinanceiros, custoCompra, precoFormulacao, moeda, dataUltimoAumento, qtdeMinimaEstoque, pesoEmbalagem, corPadrao, naturezaQuimica, versaoArteGrafica, mgKg, altura, ncm, origemMercadoriaPadrao, passaQualidade, ultimoValor, qtdeMaximaEstoque, dataUltimaEntrada, densidadePesoEspecifico, percentualDiferimento, material, purezaMateriaPrima, largura, dl50Oral]);

    const buildOriginalDataObject = useCallback(() => {
        if (!editingItem) return null;
        return {
            code: editingItem.code || '',
            commercialName: editingItem.commercialName || '',
            inciName: editingItem.inciName || '',
            inciNamePt: editingItem.inciNamePt || '',
            situation: editingItem.situation || DEFAULT_SITUATION,
            casNumber: editingItem.casNumber || '',
            productTypeIds: editingItem.productTypeIds || [],
            categoryIds: editingItem.categoryIds || [],
            qualityCriteria: editingItem.qualityCriteria || [],
            codigoBarras: editingItem.codigoBarras || '',
            matInsumoInterno: editingItem.matInsumoInterno || '',
            unidadeMedidaInterna: editingItem.unidadeMedidaInterna || '',
            encargosFinanceiros: editingItem.encargosFinanceiros || 0,
            custoCompra: editingItem.custoCompra || 0,
            precoFormulacao: editingItem.precoFormulacao || 0,
            moeda: editingItem.moeda || 'BRL',
            dataUltimoAumento: editingItem.dataUltimoAumento || '',
            qtdeMinimaEstoque: editingItem.qtdeMinimaEstoque || 0,
            pesoEmbalagem: editingItem.pesoEmbalagem || 0,
            corPadrao: editingItem.corPadrao || '',
            naturezaQuimica: editingItem.naturezaQuimica || '',
            versaoArteGrafica: editingItem.versaoArteGrafica || '',
            mgKg: editingItem.mgKg || 0,
            altura: editingItem.altura || 0,
            ncm: editingItem.ncm || '',
            origemMercadoriaPadrao: editingItem.origemMercadoriaPadrao ?? DEFAULT_ORIGEM_ICMS,
            passaQualidade: editingItem.passaQualidade || false,
            ultimoValor: editingItem.ultimoValor || 0,
            qtdeMaximaEstoque: editingItem.qtdeMaximaEstoque || 0,
            dataUltimaEntrada: editingItem.dataUltimaEntrada || '',
            densidadePesoEspecifico: editingItem.densidadePesoEspecifico || 0,
            percentualDiferimento: editingItem.percentualDiferimento || 0,
            material: editingItem.material || '',
            purezaMateriaPrima: editingItem.purezaMateriaPrima || 0,
            largura: editingItem.largura || 0,
            dl50Oral: editingItem.dl50Oral || '',
            suppliers: (editingItem.suppliers || []).map(s => ({
                ...s,
                valor: s.valor || 0,
                pesoEspecifico: s.pesoEspecifico || 0,
                leadTime: s.leadTime || 0,
                loteMinimo: s.loteMinimo || 0,
            })),
        };
    }, [editingItem]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) return;

        setFormError('');
        setFormSuccess('');

        if (!commercialName.trim() || selectedProductTypeIds.length === 0 || selectedCategoryIds.length === 0 || !inciName.trim()) {
            setFormError(t('pages.rawMaterials.form.validationRequired'));
            return;
        }

        if (!code.trim()) {
            setFormError(t('pages.rawMaterials.form.codeRequired'));
            return;
        }

        if (!editingItem && isCodeDuplicate(code.trim())) {
            setFormError(t('pages.rawMaterials.form.codeExists', { code }));
            return;
        }

        if (editingItem && isCodeDuplicate(code.trim(), editingItem.id)) {
            setFormError(t('pages.rawMaterials.form.codeExists', { code }));
            return;
        }

        if (editingItem) {
            const currentData = buildCurrentDataObject();
            const originalData = buildOriginalDataObject();
            const hasChanges = hasDataChanged(currentData, originalData);
            if (!hasChanges) {
                addToast(t('pages.rawMaterials.noChangesDetected'), 'info');
                resetForm();
                return;
            }
        }

        const formattedSuppliers = selectedSuppliers.map(s => ({
            ...s,
            valor: parseFloat(String(s.valor).replace(',', '.')) || 0,
            pesoEspecifico: parseFloat(String(s.pesoEspecifico).replace(',', '.')) || 0,
            leadTime: parseInt(s.leadTime, 10) || 0,
            loteMinimo: parseFloat(String(s.loteMinimo).replace(',', '.')) || 0,
        }));

        const data = {
            code: code.trim(),
            commercialName: commercialName.trim(),
            inciName: inciName.trim(),
            inciNamePt: inciNamePt.trim(),
            situation: situation,
            casNumber: casNumber.trim(),
            productTypeIds: selectedProductTypeIds,
            categoryIds: selectedCategoryIds,
            qualityCriteria: selectedCriteria,
            codigoBarras: codigoBarras.trim(),
            matInsumoInterno: matInsumoInterno.trim(),
            unidadeMedidaInterna: unidadeMedidaInterna,
            encargosFinanceiros: parseFloat(encargosFinanceiros) || 0,
            custoCompra: parseFloat(custoCompra) || 0,
            precoFormulacao: parseFloat(precoFormulacao) || 0,
            moeda: moeda,
            dataUltimoAumento: dataUltimoAumento,
            qtdeMinimaEstoque: parseFloat(qtdeMinimaEstoque) || 0,
            pesoEmbalagem: parseFloat(pesoEmbalagem) || 0,
            corPadrao: corPadrao,
            naturezaQuimica: naturezaQuimica.trim(),
            versaoArteGrafica: versaoArteGrafica.trim(),
            mgKg: parseFloat(mgKg) || 0,
            altura: parseFloat(altura) || 0,
            ncm: ncm,
            origemMercadoriaPadrao: parseInt(origemMercadoriaPadrao) || DEFAULT_ORIGEM_ICMS,
            passaQualidade: passaQualidade,
            ultimoValor: parseFloat(ultimoValor) || 0,
            qtdeMaximaEstoque: parseFloat(qtdeMaximaEstoque) || 0,
            dataUltimaEntrada: dataUltimaEntrada,
            densidadePesoEspecifico: parseFloat(densidadePesoEspecifico) || 0,
            percentualDiferimento: parseFloat(percentualDiferimento) || 0,
            material: material.trim(),
            purezaMateriaPrima: parseFloat(purezaMateriaPrima) || 0,
            largura: parseFloat(largura) || 0,
            dl50Oral: dl50Oral.trim(),
            suppliers: formattedSuppliers,
            createdAt: editingItem ? editingItem.createdAt : new Date(),
            updatedAt: new Date(),
        };

        setIsSubmitting(true);

        try {
            let result = editingItem
                ? await updateDocument(editingItem.id, data)
                : await addDocument(data);

            if (result.success) {
                addToast(
                    editingItem ? t('pages.rawMaterials.updateSuccess') : t('pages.rawMaterials.createSuccess'),
                    'success'
                );
                setFormSuccess(editingItem ? t('pages.rawMaterials.updateSuccess') : t('pages.rawMaterials.createSuccess'));
                setTimeout(() => {
                    resetForm();
                }, 1500);
            } else {
                setFormError(t('pages.rawMaterials.saveError'));
                addToast(t('pages.rawMaterials.saveError'), 'error');
                setIsSubmitting(false);
            }
        } catch (error) {
            setFormError(t('pages.rawMaterials.saveError'));
            addToast(t('pages.rawMaterials.saveError'), 'error');
            setIsSubmitting(false);
        }
    };

    // --- Filtering and Pagination ---
    const filteredRawMaterials = useMemo(() => {
        if (!rawMaterials) return [];
        const lowerSearch = debouncedSearchTerm.toLowerCase();
        return rawMaterials.filter(item => {
            const matchesSearchTerm = (
                (item.commercialName && item.commercialName.toLowerCase().includes(lowerSearch)) ||
                (item.inciName && item.inciName.toLowerCase().includes(lowerSearch)) ||
                (item.code && item.code.toLowerCase().includes(lowerSearch))
            );
            const matchesProductType = !filterProductTypeId || (item.productTypeIds && item.productTypeIds.includes(filterProductTypeId));
            const matchesCategory = !filterCategoryId || (item.categoryIds && item.categoryIds.includes(filterCategoryId));
            return matchesSearchTerm && matchesProductType && matchesCategory;
        });
    }, [rawMaterials, debouncedSearchTerm, filterProductTypeId, filterCategoryId]);

    const {
        currentPage,
        totalPages,
        currentItems: currentRawMaterials,
        goToPage,
        resetPage,
    } = usePagination(filteredRawMaterials, ITEMS_PER_PAGE);

    useEffect(() => {
        resetPage();
    }, [searchTerm, filterProductTypeId, filterCategoryId, resetPage]);

    const handleFilterChange = (setter, value) => {
        setter(value);
        resetPage();
    };

    const handleDelete = async (item) => {
        if (!can?.delete) {
            addToast(t('pages.rawMaterials.noPermissionDelete'), 'error');
            return;
        }

        if (window.confirm(t('pages.rawMaterials.deleteConfirm', { name: item.commercialName }))) {
            setIsSubmitting(true);
            try {
                const result = await deleteDocument(item.id);
                if (result.success) {
                    addToast(t('pages.rawMaterials.deleteSuccess'), 'success');
                } else {
                    addToast(t('pages.rawMaterials.deleteError'), 'error');
                }
            } catch (error) {
                addToast(t('pages.rawMaterials.deleteError'), 'error');
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const areModalParamsLoading = shouldLoadModalParams && (!suffixes || !allSuppliers || !allUnits || !colorsList);

    // --- Modal Content ---
    const renderModalContent = () => {
        const uomOptions = allUnits ? allUnits.map(u => ({ id: u.id, name: u.name })) : [];
        const colorOptions = colorsList ? colorsList.map(c => ({ id: c.id, name: c.name })) : [];

        const productTypeOptions = productTypes?.map(t => ({ id: t.id, name: t.name })) || [];
        const categoryOptions = sortedCategories?.map(c => ({ id: c.id, name: c.name })) || [];

        return (
            <>
                {/* Seção 1: Informações Básicas */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FiPackage size={18} /></div>
                        <h3 className={styles.sectionTitle}>{t('pages.rawMaterials.form.basicInfo')}</h3>
                        <span className={styles.sectionBadge}>{t('common.required')}</span>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label>
                                {t('pages.rawMaterials.form.code')} <span className={styles.required}>*</span>
                            </label>
                            {editingItem ? (
                                <input
                                    type="text"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    className={styles.input}
                                    placeholder={t('pages.rawMaterials.form.codePlaceholder')}
                                    disabled={isSubmitting}
                                />
                            ) : (
                                <>
                                    <select
                                        value={selectedPrefix}
                                        onChange={(e) => setSelectedPrefix(e.target.value)}
                                        className={styles.select}
                                        required
                                        disabled={isSubmitting}
                                    >
                                        <option value="" disabled>{t('pages.rawMaterials.form.selectSuffix')}</option>
                                        {suffixes && suffixes.map(s => (
                                            <option key={s.id} value={s.name}>{s.name}</option>
                                        ))}
                                    </select>
                                    {selectedPrefix && (
                                        <small className={styles.suggestionHint}>
                                            {t('pages.rawMaterials.form.codeGenerated', { code: previewGeneratedCode })}
                                        </small>
                                    )}
                                </>
                            )}
                        </div>
                        <div className={styles.inputGroup}>
                            <label>
                                {t('pages.rawMaterials.form.commercialName')} <span className={styles.required}>*</span>
                            </label>
                            <input
                                type="text"
                                value={commercialName}
                                onChange={(e) => setCommercialName(e.target.value)}
                                className={styles.input}
                                placeholder={t('pages.rawMaterials.form.commercialNamePlaceholder')}
                                required
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>
                                {t('pages.rawMaterials.form.inciName')} <span className={styles.required}>*</span>
                            </label>
                            <input
                                type="text"
                                value={inciName}
                                onChange={(e) => setInciName(e.target.value)}
                                className={styles.input}
                                placeholder={t('pages.rawMaterials.form.inciNamePlaceholder')}
                                required
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.rawMaterials.form.inciNamePt')}</label>
                            <input
                                type="text"
                                value={inciNamePt}
                                onChange={(e) => setInciNamePt(e.target.value)}
                                className={styles.input}
                                placeholder={t('pages.rawMaterials.form.inciNamePtPlaceholder')}
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.rawMaterials.form.casNumber')}</label>
                            <input
                                type="text"
                                value={casNumber}
                                onChange={(e) => setCasNumber(e.target.value)}
                                className={styles.input}
                                placeholder={t('pages.rawMaterials.form.casNumberPlaceholder')}
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.rawMaterials.form.situation')}</label>
                            <select
                                value={situation}
                                onChange={(e) => setSituation(e.target.value)}
                                className={styles.select}
                                disabled={isSubmitting}
                            >
                                {SITUATION_OPTIONS.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Seção 2: Classificação - Using TagInput */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FaTags size={18} /></div>
                        <h3 className={styles.sectionTitle}>{t('pages.rawMaterials.form.classification')}</h3>
                    </div>
                    <div className={styles.classificationGrid}>
                        <TagInput
                            label={t('pages.rawMaterials.form.productTypes')}
                            options={productTypeOptions}
                            selectedIds={selectedProductTypeIds}
                            onAdd={handleAddProductType}
                            onRemove={handleRemoveProductType}
                            placeholder={t('pages.rawMaterials.form.addProductType')}
                            disabled={isSubmitting}
                            required
                            getOptionLabel={(item) => item.name}
                            getOptionId={(item) => item.id}
                        />
                        <TagInput
                            label={t('pages.rawMaterials.form.categories')}
                            options={categoryOptions}
                            selectedIds={selectedCategoryIds}
                            onAdd={handleAddCategory}
                            onRemove={handleRemoveCategory}
                            placeholder={t('pages.rawMaterials.form.addCategory')}
                            disabled={isSubmitting}
                            required
                            getOptionLabel={(item) => item.name}
                            getOptionId={(item) => item.id}
                        />
                    </div>
                </div>

                {/* Seção 3: Fornecedores - Using Combobox */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FaBuilding size={18} /></div>
                        <h3 className={styles.sectionTitle}>{t('pages.rawMaterials.form.suppliers')}</h3>
                        <span className={styles.sectionBadge}>{t('common.optional')}</span>
                    </div>
                    <div style={{ padding: '20px' }}>
                        <div className={styles.formRow}>
                            <div className={styles.inputGroup}>
                                <label>{t('pages.rawMaterials.form.addSupplier')}</label>
                                <ComboBox
                                    options={supplierOptions}
                                    value={selectedSupplierId}
                                    onChange={handleSupplierSelect}
                                    placeholder={t('pages.rawMaterials.form.searchSupplierPlaceholder')}
                                    disabled={isSubmitting}
                                    noResultsMessage={t('pages.rawMaterials.form.noSuppliersAvailable')}
                                    getOptionLabel={(option) => `${option.codigo || '-'} - ${option.razaoSocial}`}
                                    getOptionValue={(option) => option.id}
                                    getOptionKey={(option) => option.id}
                                />
                            </div>
                        </div>

                        <div className={styles.tableContainer} style={{ marginTop: '20px' }}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>{t('pages.rawMaterials.form.supplierCode')}</th>
                                        <th>{t('pages.rawMaterials.form.supplierName')}</th>
                                        <th>{t('pages.rawMaterials.form.supplierIcmsOrigin')}</th>
                                        <th>{t('pages.rawMaterials.form.supplierUnit')}</th>
                                        <th>{t('pages.rawMaterials.form.supplierValue')}</th>
                                        <th style={{ width: '100px' }}>{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedSuppliers.map(sup => (
                                        <tr key={sup.supplierId}>
                                            <td><strong>{sup.codigo || '-'}</strong></td>
                                            <td>{sup.razaoSocial}</td>
                                            <td>
                                                <select
                                                    value={sup.origemIcms}
                                                    onChange={(e) => handleSupplierDataChange(sup.supplierId, 'origemIcms', e.target.value)}
                                                    className={styles.select}
                                                    style={{ minWidth: '200px' }}
                                                    disabled={isSubmitting}
                                                >
                                                    {ORIGEM_MERCADORIA_ICMS_OPTIONS.map(o => (
                                                        <option key={o.value} value={o.value}>{o.label}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <select
                                                    value={sup.unitOfMeasure}
                                                    onChange={(e) => handleSupplierDataChange(sup.supplierId, 'unitOfMeasure', e.target.value)}
                                                    className={styles.select}
                                                    disabled={isSubmitting}
                                                >
                                                    <option value="">{t('common.select')}</option>
                                                    {uomOptions.map(o => (
                                                        <option key={o.id} value={o.id}>{o.name}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={sup.valor}
                                                    onChange={(e) => handleSupplierDataChange(sup.supplierId, 'valor', e.target.value)}
                                                    className={styles.input}
                                                    placeholder="0,00"
                                                    disabled={isSubmitting}
                                                />
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveSupplier(sup.supplierId)}
                                                    className={`${styles.actionButton} ${styles.deleteButton}`}
                                                    disabled={isSubmitting}
                                                    title={t('pages.rawMaterials.form.removeSupplier')}
                                                >
                                                    <FiTrash2 size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {selectedSuppliers.length === 0 && (
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                                                {t('pages.rawMaterials.form.noSuppliersAdded')}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Seção 4: Dados Fiscais */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FiDollarSign size={18} /></div>
                        <h3 className={styles.sectionTitle}>{t('pages.rawMaterials.form.fiscalData')}</h3>
                    </div>
                    <div style={{ padding: '20px' }}>
                        <div className={styles.formRow} style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                            <div className={styles.inputGroup}>
                                <label>{t('pages.rawMaterials.form.unitOfMeasure')}</label>
                                <select
                                    value={unidadeMedidaInterna}
                                    onChange={(e) => setUnidadeMedidaInterna(e.target.value)}
                                    className={styles.select}
                                    disabled={isSubmitting}
                                >
                                    <option value="">{t('common.select')}</option>
                                    {uomOptions.map(o => (
                                        <option key={o.id} value={o.id}>{o.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.inputGroup}>
                                <label>{t('pages.rawMaterials.form.financialCharges')}</label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={encargosFinanceiros}
                                    onChange={(e) => setEncargosFinanceiros(e.target.value)}
                                    className={styles.input}
                                    placeholder="0,00"
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        <div className={styles.formRow} style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginTop: '16px' }}>
                            <div className={styles.inputGroup}>
                                <label>{t('pages.rawMaterials.form.purchaseCost')}</label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={custoCompra}
                                    onChange={(e) => setCustoCompra(e.target.value)}
                                    className={styles.input}
                                    placeholder="0,00"
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div className={styles.inputGroup}>
                                <label>{t('pages.rawMaterials.form.formulationPrice')}</label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={precoFormulacao}
                                    onChange={(e) => setPrecoFormulacao(e.target.value)}
                                    className={styles.input}
                                    placeholder="0,00"
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        <div className={styles.formRow} style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginTop: '16px' }}>
                            <div className={styles.inputGroup}>
                                <label>{t('pages.rawMaterials.form.currency')}</label>
                                <select
                                    value={moeda}
                                    onChange={(e) => setMoeda(e.target.value)}
                                    className={styles.select}
                                    disabled={isSubmitting}
                                >
                                    {CURRENCY_OPTIONS.map(o => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.inputGroup}>
                                <label>{t('pages.rawMaterials.form.lastIncreaseDate')}</label>
                                <input
                                    type="date"
                                    value={dataUltimoAumento}
                                    onChange={(e) => setDataUltimoAumento(e.target.value)}
                                    className={styles.input}
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        <div className={styles.formRow} style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginTop: '16px' }}>
                            <div className={styles.inputGroup}>
                                <label>{t('pages.rawMaterials.form.minStock')}</label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={qtdeMinimaEstoque}
                                    onChange={(e) => setQtdeMinimaEstoque(e.target.value)}
                                    className={styles.input}
                                    placeholder="0"
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div className={styles.inputGroup}>
                                <label>
                                    {t('pages.rawMaterials.form.ncm')} <span className={styles.required}>*</span>
                                </label>
                                <ComboBox
                                    options={ncmOptions}
                                    value={ncm}
                                    onChange={setNcm}
                                    placeholder={t('pages.rawMaterials.form.ncmPlaceholder') || 'Search NCM code or description...'}
                                    disabled={isSubmitting}
                                    loading={ncmLoading}
                                    noResultsMessage={t('pages.rawMaterials.form.ncmNoResults') || 'No NCM codes found'}
                                    getOptionLabel={(option) => `${option.ncmCode}${option.ncmDescription ? ` - ${option.ncmDescription}` : ''}`}
                                    getOptionValue={(option) => option.id}
                                    getOptionKey={(option) => option.id}
                                />
                            </div>
                        </div>

                        <div className={styles.formRow} style={{ gridTemplateColumns: '1fr', marginTop: '16px' }}>
                            <div className={styles.inputGroup}>
                                <label>{t('pages.rawMaterials.form.icmsOrigin')}</label>
                                <select
                                    value={origemMercadoriaPadrao}
                                    onChange={(e) => setOrigemMercadoriaPadrao(e.target.value)}
                                    className={styles.select}
                                    disabled={isSubmitting}
                                >
                                    {ORIGEM_MERCADORIA_ICMS_OPTIONS.map(o => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className={styles.formRow} style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginTop: '16px' }}>
                            <div className={styles.inputGroup}>
                                <label>{t('pages.rawMaterials.form.lastEntryDate')}</label>
                                <input
                                    type="date"
                                    value={dataUltimaEntrada}
                                    onChange={(e) => setDataUltimaEntrada(e.target.value)}
                                    className={styles.input}
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div className={styles.inputGroup}>
                                <label>{t('pages.rawMaterials.form.density')}</label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={densidadePesoEspecifico}
                                    onChange={(e) => setDensidadePesoEspecifico(e.target.value)}
                                    className={styles.input}
                                    placeholder="0,000"
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    // --- Table Render ---
    const renderTable = () => {
        if (isLoading && currentRawMaterials.length === 0) {
            return <TableSkeleton columns={9} rows={ITEMS_PER_PAGE} />;
        }

        if (currentRawMaterials.length === 0) {
            return (
                <EmptyState
                    icon="🧪"
                    title={searchTerm ? t('pages.rawMaterials.table.emptySearch') : t('pages.rawMaterials.table.empty')}
                    message={searchTerm ? t('pages.rawMaterials.search.noResults') : t('pages.rawMaterials.table.emptyMessage')}
                    actionText={can?.create ? t('pages.rawMaterials.newMaterial') : undefined}
                    onAction={can?.create ? handleNewClick : undefined}
                />
            );
        }

        return (
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th style={{ width: '80px' }}>{t('pages.rawMaterials.table.code')}</th>
                        <th>{t('pages.rawMaterials.table.commercialName')}</th>
                        <th>{t('pages.rawMaterials.table.inciName')}</th>
                        <th>{t('pages.rawMaterials.table.type')}</th>
                        <th>{t('pages.rawMaterials.table.category')}</th>
                        <th>{t('pages.rawMaterials.table.suppliers')}</th>
                        <th style={{ width: '100px' }}>{t('pages.rawMaterials.table.price')}</th>
                        <th style={{ width: '130px' }}>{t('common.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {currentRawMaterials.map((item) => (
                        <RawMaterialRow
                            key={item.id}
                            item={item}
                            productTypes={productTypes}
                            categories={categories}
                            formLoading={isSubmitting}
                            onEdit={handleEditClick}
                            onDelete={handleDelete}
                            can={can}
                            t={t}
                            ncmList={ncmList}
                        />
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <div className={styles.pageContainer}>
            {/* Toast System */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => removeToast(toast.id)}
                        />
                    ))}
                </div>
            )}

            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={resetForm}
                onSubmit={handleFormSubmit}
                title={editingItem ? t('pages.rawMaterials.editMaterial') : t('pages.rawMaterials.newMaterial')}
                subtitle={editingItem ? `${t('pages.rawMaterials.editingLabel')}: ${editingItem.commercialName} (${editingItem.code})` : undefined}
                isLoading={isSubmitting}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? t('common.saveChanges') : t('pages.rawMaterials.form.create')}
                maxWidth="900px"
            >
                {areModalParamsLoading ? (
                    <div style={{ padding: '60px', textAlign: 'center' }}>
                        <div className={styles.buttonSpinner} style={{ width: '40px', height: '40px', margin: '0 auto 20px' }}></div>
                        <p>{t('common.loading')}</p>
                    </div>
                ) : (
                    renderModalContent()
                )}
            </FormModal>

            {/* Page Header */}
            <PageHeader
                title={t('pages.rawMaterials.title')}
                subtitle={t('pages.rawMaterials.subtitle')}
                actionText={can?.create ? t('pages.rawMaterials.newMaterial') : undefined}
                onAction={handleNewClick}
                disabled={isLoading}
                showAction={can?.create}
            />

            {/* Search and Filters */}
            <div className={styles.searchBar}>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    <div className={styles.inputGroup} style={{ flex: '1' }}>
                        <label>{t('common.search')}</label>
                        <SearchBar
                            placeholder={t('pages.rawMaterials.search.placeholder')}
                            onSearch={setSearchTerm}
                            disabled={isLoading}
                            debounceDelay={300}
                            initialValue={searchTerm}
                        />
                    </div>
                    <div className={styles.inputGroup} style={{ flex: '0 0 200px' }}>
                        <label>{t('pages.rawMaterials.filters.type')}</label>
                        <select
                            value={filterProductTypeId}
                            onChange={(e) => handleFilterChange(setFilterProductTypeId, e.target.value)}
                            className={styles.select}
                            disabled={isLoading}
                        >
                            <option value="">{t('common.all')}</option>
                            {productTypes && productTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    </div>
                    <div className={styles.inputGroup} style={{ flex: '0 0 200px' }}>
                        <label>{t('pages.rawMaterials.filters.category')}</label>
                        <select
                            value={filterCategoryId}
                            onChange={(e) => handleFilterChange(setFilterCategoryId, e.target.value)}
                            className={styles.select}
                            disabled={isLoading}
                        >
                            <option value="">{t('common.all')}</option>
                            {sortedCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className={styles.tableContainer}>
                {renderTable()}
            </div>

            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredRawMaterials.length}
                onPageChange={goToPage}
                disabled={isLoading}
                itemsLabel={t('pages.rawMaterials.table.items')}
            />
        </div>
    );
};

export default RawMaterialPage;